import styled from 'styled-components/macro';

import Switch, { SwitchProps } from '@mui/material/Switch';
import FormControlLabel, {
  FormControlLabelProps
} from '@mui/material/FormControlLabel';

import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import { BaseReactComponent } from '../../../types';

export interface HiSwitchProps extends BaseReactComponent {
  /**
   * The radio label.
   */
  label?: FormControlLabelProps['label'];
  /**
   * If `true`, the component is checked.
   */
  checked?: SwitchProps['checked'];
  /**
   * The color of the component.
   */
  color?: SwitchProps['color'];
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: SwitchProps['disabled'];
  /**
   * Callback fired when the state is changed.
   */
  onChange?: SwitchProps['onChange'];
  /**
   * Override Mui Switch props
   * (https://mui.com/material-ui/api/switch/).
   */
  SwitchProps?: SwitchProps;
}

/**
 * The `HiSwitch` component is used to immediately activate or deactivate something.
 * - Text **label shouldn’t change** after tapping the toggle.
 * - If not specified, place the **toggle first** and label on the right.
 *
 * Tapping a toggle switch is a two-step action: selection and execution, whereas checkbox is just a selection of an option and its execution usually requires another control.
 *
 * It extends Mui's `Switch` component.
 *
 * [Learn when to use](https://uxplanet.org/checkbox-vs-toggle-switch-7fc6e83f10b8)
 */
const HiSwitch = ({
  label,
  checked,
  color,
  disabled,
  onChange,
  SwitchProps,
  ...other
}: HiSwitchProps) => {
  const SwitchComponent = (
    <Switch
      {...other}
      {...SwitchProps}
      checked={checked}
      color={color === 'darkBackground' ? 'primary' : color}
      disabled={disabled}
      onChange={onChange}
      checkedIcon={
        <StyledHiSwitchIcon className="HiSwitch-icon-check" disabled={disabled}>
          <Check />
        </StyledHiSwitchIcon>
      }
      icon={
        <StyledHiSwitchIcon className="HiSwitch-icon-close">
          <Close />
        </StyledHiSwitchIcon>
      }
      classes={{ track: 'HiSwitch-track', checked: 'HiSwitch-checked' }}
      sx={[
        color === 'darkBackground' &&
          ((theme) => ({
            '.HiSwitch-track': {
              bgcolor: checked
                ? theme.palette.primary.light + '!important'
                : 'grey.400',
              opacity: '1 !important'
            }
          }))
      ]}
    />
  );

  // If label, render with FormControlLabel
  if (label) {
    return (
      <FormControlLabel
        label={label}
        control={SwitchComponent}
        classes={{
          label: 'HiSwitch-label'
        }}
        sx={[
          color === 'darkBackground' && {
            '.HiSwitch-label': { color: 'white', fontWeight: 500 }
          }
        ]}
      />
    );
  }

  // If no label, render only Switch
  return SwitchComponent;
};

/**
 * Icon inside the circle of HiSwitch
 */
interface HiSwitchIconProps {
  readonly disabled?: boolean;
}
const StyledHiSwitchIcon = styled.span<HiSwitchIconProps>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  box-shadow: 0 2px 1px -1px rgba(0, 0, 0, 0.2), 0 1px 1px rgba(0, 0, 0, 0.14),
    0px 1px 3px rgba(0, 0, 0, 0.12);
  background-color: currentColor;
  border-radius: 50%;

  svg {
    width: 16px;
    height: 16px;
    border-radius: 50%;
  }

  &.HiSwitch-icon-check {
    svg {
      fill: white;
    }
  }

  &.HiSwitch-icon-close {
    background-color: ${({ theme, disabled }) =>
      disabled ? theme.palette.grey[100] : theme.palette.grey[50]};

    svg {
      fill: ${({ theme }) => theme.palette.action.disabled};
    }
  }
`;

export default HiSwitch;
